<template>
    <b-modal
        v-model="isVisible"
        :size="modalSize"
        :title="modalTitle"
        modal-class="vue-need-bootstrap"
        lazy
        centered
        scrollable
        hide-footer
        no-close-on-backdrop
    >
        <template #default="{ cancel }">
            <!-- Cancellation text -->
            <div v-if="!showReasons && allowCanceling" class="mb-3">
                You are about to cancel your account for <u>{{ store }}</u
                >.
                <b>
                    If you cancel all your templates, libraries, and personalization information will be disconnected
                    from <u>{{ store }}</u
                    >.</b
                >

                <br />
                <br />

                <div v-show="allowPausing">
                    If you wish to save this information to use it on your products later on you can
                    <b>pause your account instead for $5 a month</b>. So you can come back whenever you are ready
                    without losing all your work.
                </div>
            </div>

            <b-modal
                v-model="showPaymentFailedAlert"
                title="Payment failed"
                modal-class="vue-need-bootstrap"
                centered
                no-close-on-esc
                hide-header-close
                no-close-on-backdrop
                ok-only
                @ok="isVisible = false"
            >
                Payment failed! Please try again with a new card.
            </b-modal>

            <!-- Pausing text -->
            <div v-if="!showReasons && !allowCanceling && allowPausing" class="mb-3">
                Your products on
                <b
                    ><u>{{ store }}</u></b
                >
                will no longer have options and previews live, but all your work will be saved so you can come back
                whenever you are ready and activate them.
            </div>

            <validation-observer v-slot="{ valid }">
                <b-form @submit.stop.prevent>
                    <!-- Password prompt -->
                    <div v-if="!showReasons">
                        <validation-provider v-slot="{ valid, errors }" rules="required|min:8" name="Password">
                            <b-form-group
                                id="current-password-input"
                                label-cols="3"
                                content-cols="9"
                                label="Current password"
                                label-for="current-password-input"
                                class="my-3"
                                :invalid-feedback="invalidPassword ? 'Wrong password' : errors[0]"
                                :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                            >
                                <b-form-input
                                    id="current-password-input"
                                    v-model="password"
                                    type="password"
                                    :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                                    :disabled="processing"
                                />
                            </b-form-group>
                        </validation-provider>
                    </div>

                    <!-- Ask for cancel/pause reason -->
                    <div v-if="showReasons">
                        <div class="mb-2">
                            Please help us understand your reasons for
                            <span v-if="currentAction === 'cancel'">canceling</span
                            ><span v-if="currentAction === 'pause'">pausing</span> so we can improve our service for the
                            future:
                        </div>

                        <!-- Reasons for canceling subscription -->
                        <validation-provider v-if="allowCanceling" rules="required" name="Cancel Reason">
                            <b-form-group>
                                <b-form-checkbox-group v-model="cancelReasons" stacked>
                                    <b-form-checkbox value="WANTED_TO_TEST">Only wanted to test it</b-form-checkbox>
                                    <b-form-checkbox value="MISSING_FEATURE"
                                        >Missing a key feature I needed
                                    </b-form-checkbox>
                                    <b-form-checkbox value="SWITCHING_TOOL"
                                        >Switching to other personalization tool
                                    </b-form-checkbox>
                                    <b-form-checkbox value="NOT_SATISFIED_WITH_PRODUCTS"
                                        >I was not satisfied with the results on my products</b-form-checkbox
                                    >
                                    <b-form-checkbox value="NOT_SATISFIED_WITH_SUPPORT"
                                        >I was not satisfied with the support
                                    </b-form-checkbox>
                                    <b-form-checkbox value="TOO_COMPLICATED">Too complicated to use</b-form-checkbox>
                                    <b-form-checkbox value="TOO_EXPENSIVE">Too expensive</b-form-checkbox>
                                    <b-form-checkbox value="SHUTTING_DOWN_STORE"
                                        >I'm shutting down my store
                                    </b-form-checkbox>
                                    <b-form-checkbox value="OTHER">Other</b-form-checkbox>
                                </b-form-checkbox-group>
                            </b-form-group>
                        </validation-provider>

                        <!-- Reasons for pausing subscription -->
                        <validation-provider
                            v-if="!allowCanceling && allowPausing"
                            rules="required"
                            name="Pause Reason"
                        >
                            <b-form-group>
                                <b-form-checkbox-group v-model="pauseReasons" stacked>
                                    <b-form-checkbox value="STORE_NOT_LIVE">My store is not yet live</b-form-checkbox>
                                    <b-form-checkbox value="SALES_GONE_DOWN">My sales have gone down </b-form-checkbox>
                                    <b-form-checkbox value="BUSINESS_IS_SEASONAL"
                                        >My business is seasonal
                                    </b-form-checkbox>
                                    <b-form-checkbox value="FOCUSING_ON_NON_CUSTOMILY_PRODUCTS"
                                        >I’m focusing on other products that don't need Customily</b-form-checkbox
                                    >
                                    <b-form-checkbox value="NO_TIME_FOR_CUSTOMILY"
                                        >I don’t have the time to use Customily right now
                                    </b-form-checkbox>
                                    <b-form-checkbox value="OTHER">Other</b-form-checkbox>
                                </b-form-checkbox-group>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-if="cancelReasons.some((r) => r === 'OTHER') || pauseReasons.some((r) => r === 'OTHER')"
                            v-slot="{ valid, errors }"
                            rules="required|min:10|max:10000"
                            name="Cancellation details"
                        >
                            <b-form-group
                                id="cancel-details-input"
                                label-cols="3"
                                content-cols="9"
                                label="Cancellation details"
                                label-for="cancel-details-input"
                                class="my-3"
                                :invalid-feedback="errors[0]"
                                :state="errors[0] ? false : valid ? true : null"
                            >
                                <b-form-textarea
                                    id="cancel-detail-input"
                                    v-model="actionDetails"
                                    :state="errors[0] ? false : valid ? true : null"
                                    :disabled="processing"
                                    rows="6"
                                    max-rows="12"
                                />
                            </b-form-group>
                        </validation-provider>
                    </div>

                    <div v-show="errorMessage" class="error-message w-100 pb-3 text-center">
                        {{ errorMessage }}
                    </div>

                    <!-- Cancel/pause subscription -->
                    <div v-if="!showReasons" class="modal-footer row pb-0">
                        <b-button variant="primary" type="reset" :disabled="processing" @click="cancel">
                            I changed my mind
                        </b-button>
                        <loader-btn
                            v-show="allowPausing"
                            variant="info"
                            :disabled="!valid || processing"
                            :loading="pausingSubscription"
                            @click="goToReasons('pause')"
                        >
                            <span v-if="accountDisableMode">Pause account and save my work</span>
                            <span v-if="!accountDisableMode">Pause store and save my work</span>
                        </loader-btn>
                        <loader-btn
                            v-if="allowCanceling"
                            variant="danger"
                            loading-text="Saving"
                            :disabled="!valid || processing"
                            :loading="cancelingSubscription"
                            @click="goToReasons('cancel')"
                        >
                            <span v-if="accountDisableMode">Cancel and delete my {{ templatesCount }} templates</span>
                            <span v-if="!accountDisableMode"
                                >Cancel <b>{{ store }}</b></span
                            >
                        </loader-btn>
                    </div>

                    <!-- Final subscription cancel -->
                    <div v-if="showReasons" class="modal-footer row pb-0">
                        <b-button variant="primary" type="reset" :disabled="processing" @click="cancel">
                            I changed my mind
                        </b-button>
                        <loader-btn
                            variant="danger"
                            loading-text="Saving"
                            :disabled="!valid || processing"
                            :loading="cancelingSubscription || pausingSubscription"
                            @click="subscriptionSubmit(currentAction)"
                        >
                            {{ currentAction === 'cancel' ? 'Cancel account' : 'Pause account' }}
                        </loader-btn>
                    </div>
                </b-form>
            </validation-observer>
        </template>
    </b-modal>
</template>

<script lang="ts" setup>
import { Ref, computed, onBeforeMount, ref } from 'vue';
import { AxiosError } from 'axios';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { updateSubscription } from '@/api/billing';
import { CancelReason, PauseReason, SubscriptionStatusUpdate } from '@/api/typings/billing';
import { getProductsCount } from '@/api/product';
import { passwordCheck } from '@/api/user';
import { BModal } from 'bootstrap-vue';

const props = withDefaults(
    defineProps<{
        value: boolean;
        subscriptionId?: string;
        allowPausing?: boolean;
        allowCanceling?: boolean;
        accountDisableMode?: boolean;
        store: string;
        // If given, this card will be used to pay for subscription pausing when current subscription is past due
        cardToken?: string;
    }>(),
    {
        allowPausing: true,
        allowCanceling: true
    }
);

const emit = defineEmits(['input', 'canceled', 'paused']);

const password = ref('');
const errorMessage = ref('');
const invalidPassword = ref(false);
const templatesCount = ref(0);
const currentAction: Ref<'cancel' | 'pause'> = ref('cancel');
const showPaymentFailedAlert = ref(false);

const processing = ref(false);
const pausingSubscription = ref(false);
const cancelingSubscription = ref(false);

const showReasons = ref(false);
const cancelReasons: Ref<CancelReason[]> = ref([]);
const pauseReasons: Ref<PauseReason[]> = ref([]);
const actionDetails = ref('');

const isVisible = computed({
    get(): boolean {
        return props.value;
    },
    set(value: boolean) {
        if (value) clearDialog();
        emit('input', value);
    }
});

const modalTitle = computed(() => {
    if (showReasons.value) {
        return "We're sorry to see you go :(";
    } else {
        if (!props.allowCanceling) {
            return 'Pause Subscription';
        } else {
            return 'Cancel Subscription';
        }
    }
});

const modalSize = computed(() => {
    if (!props.allowCanceling || !props.allowPausing) return 'lg';
    return showReasons.value ? 'lg' : 'xl';
});

onBeforeMount(async () => {
    templatesCount.value = await getProductsCount();
});

const goToReasons = async (futureAction: 'cancel' | 'pause') => {
    try {
        currentAction.value = futureAction;
        processing.value = true;
        invalidPassword.value = false;

        const result = await passwordCheck(password.value);
        if (result.Valid) {
            // Valid password, show cancel reasons
            showReasons.value = true;
        } else {
            // Invalid password
            invalidPassword.value = true;
        }
    } finally {
        processing.value = false;
    }
};

const subscriptionSubmit = async (action: 'cancel' | 'pause' = 'cancel') => {
    if (!props.store) {
        console.warn(`No store given on SubscriptionPauseCancelDialog`);
        return;
    }

    try {
        processing.value = true;
        if (password.value && props.subscriptionId) {
            errorMessage.value = '';

            if (action === 'cancel') {
                cancelingSubscription.value = true;
            } else {
                pausingSubscription.value = true;
            }

            await updateSubscription(
                props.store,
                password.value,
                action === 'cancel' ? SubscriptionStatusUpdate.Cancelled : SubscriptionStatusUpdate.Paused,
                props.cardToken || undefined,
                action === 'cancel'
                    ? cancelReasons.value.map((r) => ({
                          Type: r,
                          Details: r === 'OTHER' ? actionDetails.value : ''
                      }))
                    : undefined,
                action === 'pause'
                    ? pauseReasons.value.map((r) => ({
                          Type: r,
                          Details: r === 'OTHER' ? actionDetails.value : ''
                      }))
                    : undefined
            );
            emit(action === 'cancel' ? 'canceled' : 'paused', props.subscriptionId);
            isVisible.value = false;
        }
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 402) {
            showPaymentFailedAlert.value = true;
        } else if (axiosError.response?.status === 400) {
            if (axiosError.response.data.Message) {
                if (axiosError.response.data.Message === 'Invalid Password') {
                    invalidPassword.value = true;
                } else {
                    errorMessage.value = 'Error updating subscription';
                }
            }
        } else {
            errorMessage.value = 'Error updating subscription';
            window.rumReportError(axiosError);
        }
    } finally {
        pausingSubscription.value = false;
        cancelingSubscription.value = false;
        processing.value = false;
    }
};

const clearDialog = () => {
    errorMessage.value = '';
    invalidPassword.value = false;
    password.value = '';
    showReasons.value = false;
    cancelReasons.value = [];
    pauseReasons.value = [];
    actionDetails.value = '';
    currentAction.value = 'cancel';
    showPaymentFailedAlert.value = false;
};
</script>

<style lang="scss" scoped>
.confirmation-message {
    display: flex;
    min-height: 200px;
}
</style>
